import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import {
    getAllUnits,
    getUnitCodeSuggestions,
    saveUnits,
    saveUnitSettings,
} from 'api/settings/unitService';
import LanguagesService from 'api/languages/languagesService';
import Feature from 'Services/Feature';

/**
 * Composable for managing units list with tabs (default + language translations)
 * Used in UnitSettings page and UnitDialog
 *
 * @param {Object} options
 * @param {Function} options.$t - Translation function (injected)
 * @param {Function} options.$pushToast - Toast notification function (injected)
 * @returns {Object} Unit management state and methods
 */
export function useUnits({ $t, $pushToast }) {
    const store = useStore();

    // State
    const unit = ref(0);
    const units = ref([]);
    const editableUnits = ref([]);
    const suggestions = ref([]);
    const languages = ref([]);
    const currentTab = ref('default');
    const editMode = ref(false);
    const isLoading = ref(false);
    const isLoadingEditMode = ref(false);
    const unitListSaving = ref(false);
    const tempCounter = ref(0);
    const hasErrors = ref(false);
    const controller = ref(new AbortController());

    // Computed
    const isAdmin = computed(() => store.state.user.isAdmin);

    const isEditable = computed(() => {
        return isAdmin.value && editMode.value;
    });

    const showUnitCode = computed(() => {
        return Feature.isEnabled('einvoicing-units-parameters');
    });

    // Methods
    const getLanguageIcon = (language) => {
        return `/ressources/public/img/flags/${language.flag ?? 'blank'}.png`;
    };

    const initEditableUnits = (data = units.value) => {
        editableUnits.value = JSON.parse(
            JSON.stringify(
                data.map((item) => {
                    const hasAllTranslations = languages.value.every((lang) =>
                        item._embed.translations.some(
                            (trans) =>
                                trans.lang_id === lang.id &&
                                typeof trans.id === 'number' &&
                                trans.value !== '',
                        ),
                    );
                    return hasAllTranslations
                        ? item
                        : {
                              ...item,
                              _embed: {
                                  ...item._embed,
                                  translations: [
                                      ...item._embed.translations.filter(
                                          (translation) => typeof translation.id === 'number',
                                      ),
                                      ...languages.value
                                          .filter(
                                              (lang) =>
                                                  !item._embed.translations.some(
                                                      (trans) => trans.lang_id === lang.id,
                                                  ),
                                          )
                                          .map((lang) => ({
                                              id: null,
                                              lang_id: lang.id,
                                              value: '',
                                          })),
                                  ],
                              },
                          };
                }),
            ),
        );
    };

    const startEditMode = async () => {
        isLoadingEditMode.value = true;
        if (showUnitCode.value) {
            try {
                const { data } = await getUnitCodeSuggestions(
                    units.value.map((unit) => {
                        return {
                            label: unit.label,
                        };
                    }),
                );
                suggestions.value = data;
            } catch {
                $pushToast({
                    type: 'error',
                    icon: true,
                    text: $t.__('settings.units.suggestions.fetch_error'),
                });
            }
        }

        editableUnits.value = editableUnits.value.map((unit) => {
            let suggestion = null;
            if (suggestions.value.length) {
                suggestion = suggestions.value.find(
                    (item) => item.label === unit.label && item.code,
                );
            }
            return {
                ...unit,
                parameters: {
                    ...unit.parameters,
                    code: unit.parameters?.code || '',
                },
                _embed: {
                    ...unit._embed,
                    suggestion,
                },
            };
        });

        isLoadingEditMode.value = false;
        editMode.value = true;
    };

    const handleAddUnit = () => {
        editableUnits.value.push({
            id: `temp-${tempCounter.value++}`,
            label: '',
            is_active: true,
            parameters: { code: '' },
            _embed: {
                suggestion: null,
                isLoading: false,
                translations: languages.value.map((lang) => {
                    return {
                        id: null,
                        lang_id: lang.id,
                        value: '',
                    };
                }),
            },
        });
    };

    const handleRemoveUnit = (unitId) => {
        editableUnits.value = editableUnits.value.filter((unit) => unit.id !== unitId);
    };

    const handleSortUpdate = (sortedUnits) => {
        editableUnits.value = sortedUnits;
    };

    const handleUpdateDefaultUnit = (data) => {
        unit.value = data;
        controller.value.abort();
        controller.value = new AbortController();
        saveUnitSettings(
            {
                unit_id: data,
            },
            controller.value.signal,
        )
            .then(() => {
                $pushToast({
                    type: 'success',
                    icon: true,
                    text: $t.__('settings.units.default_unit.save_success'),
                });
            })
            .catch(() => {
                $pushToast({
                    type: 'error',
                    icon: true,
                    text: $t.__('settings.units.default_unit.save_error'),
                });
            });
    };

    const handleUnitListSave = () => {
        unitListSaving.value = true;
        hasErrors.value = false;
        editableUnits.value = editableUnits.value.map((unit) => {
            return {
                ...unit,
                _embed: {
                    ...unit._embed,
                    errors: [],
                },
            };
        });

        const payload = editableUnits.value.map((item) => {
            return {
                id: typeof item.id === 'string' && item.id.startsWith('temp-') ? null : item.id,
                label: item.label.trim(),
                is_active: item.is_active,
                parameters: {
                    ...item.parameters,
                    code: item.parameters.code.trim() || '',
                },
                translations: item._embed.translations
                    .filter((translation) => translation.value !== '')
                    .map((translation) => ({
                        id: translation.id,
                        lang_id: translation.lang_id,
                        value: translation.value.trim(),
                    })),
            };
        });

        return saveUnits(payload)
            .then((response) => {
                $pushToast({
                    type: 'success',
                    icon: true,
                    text: $t.__('settings.units.unit_list.save_success'),
                });
                editMode.value = false;
                units.value = response.data.data;
                initEditableUnits(response.data.data);
                return response;
            })
            .catch((error) => {
                if (error.response.status === 400) {
                    $pushToast({
                        type: 'error',
                        icon: true,
                        text: $t.__('settings.units.unit_list.save_validation_error'),
                    });
                    const details = error.response.data.error.details;
                    hasErrors.value = true;
                    for (const key of Object.keys(details)) {
                        const pieces = key.split('.');
                        const index = Number(pieces[1]);
                        const field = pieces.pop();
                        const message = details[key];

                        if (!editableUnits.value[index]._embed.errors) {
                            editableUnits.value[index]._embed.errors = [];
                        }

                        editableUnits.value[index]._embed.errors.push({
                            field,
                            message,
                        });
                        editableUnits.value = [...editableUnits.value];
                    }
                } else {
                    $pushToast({
                        type: 'error',
                        icon: true,
                        text: $t.__('settings.units.unit_list.save_error'),
                    });
                    editMode.value = false;
                    initEditableUnits();
                }
                throw error;
            })
            .finally(() => {
                unitListSaving.value = false;
            });
    };

    const handleCancelEditMode = () => {
        editMode.value = false;
        hasErrors.value = false;
        initEditableUnits();
    };

    const fetchLanguages = async () => {
        try {
            languages.value = await LanguagesService.getAllLanguages();
        } catch (error) {
            $pushToast({
                type: 'error',
                icon: true,
                text: $t.__('settings.units.unit_list.languages.fetch_error'),
            });
            throw error;
        }
    };

    const fetchUnits = async () => {
        isLoading.value = true;
        try {
            const data = await getAllUnits({
                order: 'rank',
                limit: 100,
                embed: ['translations'],
            });
            units.value = data;
            initEditableUnits(data);
            return data;
        } catch (error) {
            $pushToast({
                type: 'error',
                icon: true,
                text: $t.__('settings.units.unit_list.fetch_error'),
            });
            throw error;
        } finally {
            isLoading.value = false;
        }
    };

    const initialize = async () => {
        isLoading.value = true;
        await fetchLanguages();
        await fetchUnits();
    };

    return {
        // State
        unit,
        units,
        editableUnits,
        suggestions,
        languages,
        currentTab,
        editMode,
        isLoading,
        isLoadingEditMode,
        unitListSaving,
        hasErrors,

        // Computed
        isAdmin,
        isEditable,
        showUnitCode,

        // Methods
        getLanguageIcon,
        initEditableUnits,
        startEditMode,
        handleAddUnit,
        handleRemoveUnit,
        handleSortUpdate,
        handleUpdateDefaultUnit,
        handleUnitListSave,
        handleCancelEditMode,
        fetchLanguages,
        fetchUnits,
        initialize,
    };
}
